import React, {useContext, useEffect, useRef, useState} from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Container,
    Divider,

} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {getUserProfile, updateUser, uploadImage, deleteUser, createOrg, getOrgInfo, listOrgMembers, removeMember, leaveOrg, updateOrg, deleteOrg, addImpersonator, removeImpersonator, listImpersonators} from "../API/User";
import {AlertContext} from "../App/App";
import LocalLoader from "../Loaders/LocalLoader";
import {UserAvatar} from "./UserAvatar";
import {ConfirmDialog} from "../Components/ConfirmDialog";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {IconButton, List, ListItem, ListItemAvatar, ListItemText, Avatar, Chip} from '@mui/material';


export const UserProfile = ({client, setClient}) => {
    const navigate = useNavigate();
    const {setSystemAlert} = useContext(AlertContext);
    const saveRef = useRef();
    const [userProfile, setUserProfile] = useState({
        // email: '',
        name: '',
        phone: '',
        billingEmail: '',
        picture: '',
    });

    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    // Organization state
    const [orgInfo, setOrgInfo] = useState(null);
    const [orgMembers, setOrgMembers] = useState([]);
    const [newOrgName, setNewOrgName] = useState('');
    const [showCreateOrg, setShowCreateOrg] = useState(false);
    const [showLeaveDialog, setShowLeaveDialog] = useState(false);
    const [showDeleteOrgDialog, setShowDeleteOrgDialog] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState(null);
    const [editOrgName, setEditOrgName] = useState('');
    const [editOrgColor, setEditOrgColor] = useState('');
    const [customColor, setCustomColor] = useState('');
    const [isEditingOrg, setIsEditingOrg] = useState(false);

    // Impersonation delegation state
    const [impersonators, setImpersonators] = useState([]);
    const [newImpersonatorEmail, setNewImpersonatorEmail] = useState('');

    const orgColors = [
        '#1976d2', // blue
        '#388e3c', // green
        '#d32f2f', // red
        '#f57c00', // orange
        '#7b1fa2', // purple
        '#00796b', // teal
        '#c2185b', // pink
        '#455a64', // blue grey
    ];

    const loadImpersonators = async () => {
        try {
            const data = await listImpersonators();
            if (data?.allowedImpersonators) {
                setImpersonators(data.allowedImpersonators);
            }
        } catch (err) {
            // No impersonators - that's ok
        }
    };

    const loadOrgInfo = async () => {
        try {
            const info = await getOrgInfo();
            if (info && !info.error) {
                setOrgInfo(info);
                if (info.role === 'founder') {
                    const membersData = await listOrgMembers();
                    if (membersData?.members) {
                        setOrgMembers(membersData.members);
                    }
                }
            }
        } catch (err) {
            // User has no org - that's ok
        }
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([
            getUserProfile().then((data) => {
                if (!data.picture) data.picture = client.picture;
                setUserProfile(data);
            }),
            loadOrgInfo(),
            loadImpersonators()
        ]).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
                saveRef?.current?.click();
                event.preventDefault();
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [])

    const handleChange = (event) => {
        setHasChanges(true);
        const { name, value } = event.target;
        setUserProfile({ ...userProfile, [name]: value });
    };

    const handleProfilePictureChange = (event) => {
        setLoading(true);
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = async () => {
                await uploadImage(reader.result)
                setUserProfile({ ...userProfile, picture: reader.result })
                setLoading(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (event) => {
        setLoading(true)
        event.preventDefault();
        const {name, phone, billingEmail} = userProfile;
        await updateUser({name, phone, billingEmail})
        // setSystemAlert({msg: 'Saved', type: 'success', duration: 1000})
        setHasChanges(false);
        setLoading(false)
    };

    const handleDeleteAccount = async () => {
        setLoading(true);
        try {
            await deleteUser();
            setSystemAlert({msg: 'Account deleted successfully', type: 'success', duration: 3000});
            localStorage.clear();
            navigate('/');
        } catch (error) {
            setSystemAlert({msg: 'Error deleting account', type: 'error', duration: 3000});
        } finally {
            setLoading(false);
            setShowDeleteDialog(false);
        }
    };

    // Organization handlers
    const handleCreateOrg = async () => {
        if (!newOrgName.trim()) return;
        setLoading(true);
        try {
            const result = await createOrg(newOrgName.trim());
            if (result?.orgId) {
                setSystemAlert({msg: 'Organization created!', type: 'success', duration: 2000});
                setShowCreateOrg(false);
                setNewOrgName('');
                await loadOrgInfo();
            } else {
                setSystemAlert({msg: result?.error || 'Failed to create organization', type: 'error', duration: 3000});
            }
        } catch (error) {
            setSystemAlert({msg: 'Error creating organization', type: 'error', duration: 3000});
        } finally {
            setLoading(false);
        }
    };

    const handleCopyInviteLink = () => {
        const link = `${window.location.origin}/join/${orgInfo.orgId}`;
        navigator.clipboard.writeText(link);
        setSystemAlert({msg: 'Invite link copied!', type: 'success', duration: 2000});
    };

    const handleRemoveMember = async () => {
        if (!memberToRemove) return;
        setLoading(true);
        try {
            await removeMember(memberToRemove.userId);
            setSystemAlert({msg: 'Member removed', type: 'success', duration: 2000});
            setMemberToRemove(null);
            await loadOrgInfo();
        } catch (error) {
            setSystemAlert({msg: 'Error removing member', type: 'error', duration: 3000});
        } finally {
            setLoading(false);
        }
    };

    const handleLeaveOrg = async () => {
        setLoading(true);
        try {
            await leaveOrg();
            setSystemAlert({msg: 'Left organization', type: 'success', duration: 2000});
            setShowLeaveDialog(false);
            setOrgInfo(null);
            setOrgMembers([]);
        } catch (error) {
            setSystemAlert({msg: 'Error leaving organization', type: 'error', duration: 3000});
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOrg = async () => {
        setLoading(true);
        try {
            await deleteOrg();
            setSystemAlert({msg: 'Organization deleted', type: 'success', duration: 2000});
            setShowDeleteOrgDialog(false);
            setOrgInfo(null);
            setOrgMembers([]);
        } catch (error) {
            setSystemAlert({msg: 'Error deleting organization', type: 'error', duration: 3000});
        } finally {
            setLoading(false);
        }
    };

    const handleStartEditOrg = () => {
        setEditOrgName(orgInfo?.orgName || '');
        const currentColor = orgInfo?.orgColor || '';
        setEditOrgColor(currentColor);
        // If current color is not in preset colors, set it as custom
        if (currentColor && !orgColors.includes(currentColor)) {
            setCustomColor(currentColor);
        } else {
            setCustomColor('');
        }
        setIsEditingOrg(true);
    };

    const handleSaveOrg = async () => {
        setLoading(true);
        try {
            const updates = {};
            if (editOrgName && editOrgName !== orgInfo?.orgName) {
                updates.orgName = editOrgName;
            }
            // Allow clearing color (empty string) or changing to new color
            if (editOrgColor !== orgInfo?.orgColor) {
                updates.orgColor = editOrgColor; // Can be empty string to clear
            }
            if (Object.keys(updates).length > 0) {
                await updateOrg(updates);
                setSystemAlert({msg: 'Organization updated!', type: 'success', duration: 2000});
                await loadOrgInfo();
            }
            setIsEditingOrg(false);
        } catch (error) {
            setSystemAlert({msg: 'Error updating organization', type: 'error', duration: 3000});
        } finally {
            setLoading(false);
        }
    };


    // Impersonation delegation handlers
    const handleAddImpersonator = async () => {
        if (!newImpersonatorEmail.trim()) return;
        setLoading(true);
        try {
            const result = await addImpersonator(newImpersonatorEmail.trim());
            if (result?.success) {
                setSystemAlert({msg: 'Impersonator added!', type: 'success', duration: 2000});
                setNewImpersonatorEmail('');
                setImpersonators(result.allowedImpersonators);
            } else {
                setSystemAlert({msg: result?.error || 'Failed to add impersonator', type: 'error', duration: 3000});
            }
        } catch (error) {
            setSystemAlert({msg: 'Error adding impersonator', type: 'error', duration: 3000});
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveImpersonator = async (emailToRemove) => {
        setLoading(true);
        try {
            const result = await removeImpersonator(emailToRemove);
            if (result?.success) {
                setSystemAlert({msg: 'Impersonator removed', type: 'success', duration: 2000});
                setImpersonators(result.allowedImpersonators);
            } else {
                setSystemAlert({msg: result?.error || 'Failed to remove impersonator', type: 'error', duration: 3000});
            }
        } catch (error) {
            setSystemAlert({msg: 'Error removing impersonator', type: 'error', duration: 3000});
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container>
            <LocalLoader open={loading}/>
            <Box mt={4} display="flex" flexDirection="column" alignItems="center">
                <Typography component="h1" variant="h5" color="primary">
                    User Profile
                </Typography>
                <input
                    type="file"
                    id="profile-picture-input"
                    style={{ display: 'none' }}
                    onChange={handleProfilePictureChange}
                />

                <UserAvatar
                    picture={userProfile.picture}
                    sx={{ width: 100, height: 100, mt: 2, mb: 2, cursor: 'pointer' }}
                    onClick={() => document.getElementById('profile-picture-input').click()}
                    />

                <Box component="form" noValidate autoComplete="off">
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="name"
                        label="Name"
                        name="name"
                        value={userProfile.name || ''}
                        onChange={handleChange}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        disabled={true}
                        id="email"
                        label="Email Address"
                        name="email"
                        value={client.email || ''}
                        onChange={handleChange}
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        id="billingEmail"
                        label="Billing Email"
                        name="billingEmail"
                        type="email"
                        value={userProfile.billingEmail || ''}
                        onChange={handleChange}
                        helperText="Invoices will also be sent to this email"
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="phone"
                        label="Phone Number"
                        name="phone"
                        value={userProfile.phone || ''}
                        onChange={handleChange}
                    />
                        <Button
                                variant={hasChanges ? 'contained' : 'outlined'}
                                fullWidth
                                disabled={loading}
                                color="secondary"
                                style={{marginBottom: '16px'}}
                                sx={{ mt: 3, mb: 2 }}
                                onClick={handleSubmit}
                                ref={saveRef}
                        >
                            Save
                        </Button>

                        {/* Organization Section */}
                        <Divider sx={{ mt: 4, mb: 3 }} />

                        <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupIcon /> Organization
                        </Typography>

                        {!orgInfo && !showCreateOrg && (
                            <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onClick={() => setShowCreateOrg(true)}
                                sx={{ mt: 2 }}
                            >
                                Create Organization
                            </Button>
                        )}

                        {showCreateOrg && !orgInfo && (
                            <Box sx={{ mt: 2, width: '100%' }}>
                                <TextField
                                    fullWidth
                                    label="Organization Name"
                                    value={newOrgName}
                                    onChange={(e) => setNewOrgName(e.target.value)}
                                    sx={{ mb: 2 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button variant="contained" onClick={handleCreateOrg} disabled={!newOrgName.trim()}>
                                        Create
                                    </Button>
                                    <Button variant="outlined" onClick={() => { setShowCreateOrg(false); setNewOrgName(''); }}>
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {orgInfo && (
                            <Box sx={{ mt: 2, width: '100%' }}>
                                {!isEditingOrg ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {orgInfo.orgColor && (
                                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: orgInfo.orgColor }} />
                                            )}
                                            <Typography variant="h6">{orgInfo.orgName}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {orgInfo.role === 'founder' && (
                                                <Button size="small" onClick={handleStartEditOrg}>Edit</Button>
                                            )}
                                            <Chip
                                                label={orgInfo.role === 'founder' ? 'Founder' : 'Member'}
                                                color={orgInfo.role === 'founder' ? 'primary' : 'default'}
                                                size="small"
                                            />
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ mb: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="Organization Name"
                                            value={editOrgName}
                                            onChange={(e) => setEditOrgName(e.target.value)}
                                            sx={{ mb: 2 }}
                                            size="small"
                                        />
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Brand Color</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                            {/* No color option */}
                                            <Box
                                                onClick={() => { setEditOrgColor(''); setCustomColor(''); }}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    bgcolor: 'transparent',
                                                    cursor: 'pointer',
                                                    border: !editOrgColor ? '3px solid #1976d2' : '1px solid #ccc',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    '&:hover': { opacity: 0.8 }
                                                }}
                                                title="No color"
                                            >
                                                <Typography sx={{ fontSize: 18, color: '#999' }}>✕</Typography>
                                            </Box>
                                            {orgColors.map((color) => (
                                                <Box
                                                    key={color}
                                                    onClick={() => { setEditOrgColor(color); setCustomColor(''); }}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '50%',
                                                        bgcolor: color,
                                                        cursor: 'pointer',
                                                        border: editOrgColor === color && !customColor ? '3px solid white' : 'none',
                                                        boxShadow: editOrgColor === color && !customColor ? `0 0 0 2px ${color}` : 'none',
                                                        '&:hover': { opacity: 0.8 }
                                                    }}
                                                />
                                            ))}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                                                <input
                                                    type="color"
                                                    value={customColor || editOrgColor || '#1976d2'}
                                                    onChange={(e) => {
                                                        setCustomColor(e.target.value);
                                                        setEditOrgColor(e.target.value);
                                                    }}
                                                    style={{
                                                        width: 32,
                                                        height: 32,
                                                        padding: 0,
                                                        border: customColor ? '3px solid white' : '1px solid #ccc',
                                                        borderRadius: '50%',
                                                        cursor: 'pointer',
                                                        boxShadow: customColor ? `0 0 0 2px ${customColor}` : 'none',
                                                    }}
                                                    title="Custom color"
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button variant="contained" size="small" onClick={handleSaveOrg}>Save</Button>
                                            <Button variant="outlined" size="small" onClick={() => setIsEditingOrg(false)}>Cancel</Button>
                                        </Box>
                                    </Box>
                                )}

                                {orgInfo.role === 'founder' && !isEditingOrg && (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}>
                                                {`${window.location.origin}/join/${orgInfo.orgId}`}
                                            </Typography>
                                            <IconButton size="small" onClick={handleCopyInviteLink} title="Copy invite link">
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Box>

                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            Members ({orgMembers.length})
                                        </Typography>

                                        {orgMembers.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No members yet. Share the invite link to add members.
                                            </Typography>
                                        ) : (
                                            <List dense>
                                                {orgMembers.map((member) => (
                                                    <ListItem
                                                        key={member.userId}
                                                        secondaryAction={
                                                            <IconButton edge="end" onClick={() => setMemberToRemove(member)} title="Remove member">
                                                                <PersonRemoveIcon />
                                                            </IconButton>
                                                        }
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar src={member.picture} sx={{ width: 32, height: 32 }}>
                                                                {member.name?.[0]}
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={member.name || 'Unknown'}
                                                            secondary={member.email}
                                                            primaryTypographyProps={{ fontSize: 14 }}
                                                            secondaryTypographyProps={{ fontSize: 12 }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}

                                        <Button
                                            variant="outlined"
                                            color="error"
                                            fullWidth
                                            onClick={() => setShowDeleteOrgDialog(true)}
                                            sx={{ mt: 3 }}
                                            size="small"
                                        >
                                            Delete Organization
                                        </Button>
                                    </>
                                )}

                                {orgInfo.role === 'member' && (
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        fullWidth
                                        onClick={() => setShowLeaveDialog(true)}
                                        sx={{ mt: 2 }}
                                    >
                                        Leave Organization
                                    </Button>
                                )}
                            </Box>
                        )}

                        {/* Impersonation Access Section */}
                        <Divider sx={{ mt: 4, mb: 3 }} />

                        <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SupervisorAccountIcon /> Impersonation Access
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Allow other users to sign in as you using the ?ip= parameter.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Email address"
                                value={newImpersonatorEmail}
                                onChange={(e) => setNewImpersonatorEmail(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddImpersonator(); }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleAddImpersonator}
                                disabled={!newImpersonatorEmail.trim() || loading}
                                startIcon={<PersonAddIcon sx={{ color: 'white' }} />}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                Add
                            </Button>
                        </Box>

                        {impersonators.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No users have been granted impersonation access.
                            </Typography>
                        ) : (
                            <List dense>
                                {impersonators.map((impEmail) => {
                                    const loginLink = `${window.location.origin}/dashboard?ip=${encodeURIComponent(client.email)}&withLogout=1`;
                                    return (
                                        <Box key={impEmail}>
                                            <ListItem
                                                secondaryAction={
                                                    <IconButton edge="end" onClick={() => handleRemoveImpersonator(impEmail)} title="Remove impersonator">
                                                        <PersonRemoveIcon />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ width: 32, height: 32 }}>
                                                        {impEmail[0]?.toUpperCase()}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={impEmail}
                                                    primaryTypographyProps={{ fontSize: 14 }}
                                                />
                                            </ListItem>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 7, mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                                                    {loginLink}
                                                </Typography>
                                                <IconButton size="small" onClick={() => {
                                                    navigator.clipboard.writeText(loginLink);
                                                    setSystemAlert({msg: 'Login link copied!', type: 'success', duration: 2000});
                                                }} title="Copy login link">
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </List>
                        )}

                        {/* Danger Zone */}
                        <Divider sx={{ mt: 4, mb: 3 }} />

                        <Typography variant="h6" color="error" gutterBottom>
                            Danger Zone
                        </Typography>

                        <Button
                            variant="outlined"
                            fullWidth
                            disabled={loading}
                            color="error"
                            onClick={() => setShowDeleteDialog(true)}
                            sx={{ mt: 2 }}
                        >
                            Delete Account
                        </Button>
                </Box>
            </Box>
            
            <ConfirmDialog
                open={showDeleteDialog}
                title="Delete Account"
                content="Are you sure you want to delete your account? This action cannot be undone. All your data, chat history, and settings will be permanently removed."
                onConfirm={handleDeleteAccount}
                onCancel={() => setShowDeleteDialog(false)}
                confirmText="Delete Account"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={showLeaveDialog}
                title="Leave Organization"
                content={`Are you sure you want to leave "${orgInfo?.orgName}"?`}
                onConfirm={handleLeaveOrg}
                onCancel={() => setShowLeaveDialog(false)}
                confirmText="Leave"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={!!memberToRemove}
                title="Remove Member"
                content={`Are you sure you want to remove ${memberToRemove?.name || memberToRemove?.email} from your organization?`}
                onConfirm={handleRemoveMember}
                onCancel={() => setMemberToRemove(null)}
                confirmText="Remove"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={showDeleteOrgDialog}
                title="Delete Organization"
                content={`Are you sure you want to delete "${orgInfo?.orgName}"? All members will be removed and this action cannot be undone.`}
                onConfirm={handleDeleteOrg}
                onCancel={() => setShowDeleteOrgDialog(false)}
                confirmText="Delete Organization"
                cancelText="Cancel"
            />
        </Container>
    );
};
